import React, { useState, useEffect } from "react";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  getClassesAPI,
  createClassAPI,
  updateClassAPI,
  getClassTypeAPI,
  deleteClassAPI,
  getAdminClassesAPI,
} from "../../../services/portal/admin/classService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import {
  formatAmount,
  formatDate,
  formatTime,
  getStatusBadge,
  tableHeaderColor,
} from "../../../utility/Common";
import RemoveConfirmationDialog from "../../../components/portal/RemoveConfirmationDialog";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "../../../utility/RoutePaths";

const ClassManagePage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState(null);
  const [classTypes, setClassTypes] = useState([]);
  const [editingDetails, setEditingDetails] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    status: 1,
    classTypeId: 0,
    whatsAppLink: "",
    classFee: 0,
  });

  useEffect(() => {
    loadClassTypes();
  }, []);

  const loadClassTypes = async () => {
    try {
      const res = await getClassTypeAPI();
      if (res && res.IsSuccessful && res.Data) {
        setClassTypes(res.Data);
        loadClasses();
      }
    } catch (error) {
      console.error("Error loading class types:", error);
      showErrorToastMsg("Failed to load class types");
    }
  };

  const loadClasses = async () => {
    setLoading(true);
    try {
      const res = await getAdminClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        setClasses(res.Data);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
      showErrorToastMsg("Failed to load classes");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showErrorToastMsg("Please enter a class name");
      return;
    }

    if (!formData.startDate) {
      showErrorToastMsg("Please select a start date");
      return;
    }

    if (!formData.endDate) {
      showErrorToastMsg("Please select an end date");
      return;
    }

    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      showErrorToastMsg("Start date must be before end date");
      return;
    }

    if (!formData.classFee) {
      showErrorToastMsg("Please enter a class fee");
      return;
    }

    setSaving(true);

    try {
      const params = {
        name: formData.name.trim(),
        startDate: formData.startDate,
        endDate: formData.endDate,
        startTime: formData.startTime,
        endTime: formData.endTime,
        whatsAppLink: formData.whatsAppLink,
        classFee: Number(formData.classFee),
        status: Number(formData.status),
        classTypeId: Number(formData.classTypeId),
      };

      let res;
      if (editingDetails) {
        params.classId = editingDetails.ClassId;
        res = await updateClassAPI(params);
      } else {
        res = await createClassAPI(params);
      }

      if (res && res.IsSuccessful) {
        showSuccessToastMsg(
          res.Message ||
            (editingDetails
              ? "Class updated successfully"
              : "Class saved successfully"),
        );
        setEditingDetails(null);
        setFormData({
          name: "",
          startDate: "",
          endDate: "",
          status: 1,
          classTypeId: 0,
        });
        loadClasses();
      } else {
        showErrorToastMsg(
          res?.Message || "Something went wrong, Please try again later",
        );
      }
    } catch (error) {
      console.error("Error saving class:", error);
      showErrorToastMsg("Failed to save class");
    } finally {
      setSaving(false);
    }
  };

  const handleEditDetails = (classItem) => {
    setEditingDetails(classItem);
    const classDate = classItem.StartDate;
    const endDate = classItem.EndDate;

    // Format date for input (YYYY-MM-DD)
    let formattedStartDate = "";
    let formattedEndDate = "";

    if (classDate) {
      const dateObj = new Date(classDate);
      formattedStartDate = dateObj.toISOString().split("T")[0];
    }

    if (endDate) {
      const dateObj = new Date(endDate);
      formattedEndDate = dateObj.toISOString().split("T")[0];
    }

    setFormData({
      name: classItem.Name,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      startTime: classItem.StartTime,
      endTime: classItem.EndTime,
      whatsAppLink: classItem.WhatsAppLink,
      classFee: Number(classItem.ClassFee),
      status: Number(classItem.Status),
      classTypeId: Number(classItem.ClassTypeId),
    });
  };

  const handleCancelEdit = () => {
    setEditingDetails(null);
    clearFields();
  };

  const clearFields = () => {
    setFormData({
      name: "",
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      whatsAppLink: "",
      classFee: 1,
      status: 1,
      classTypeId: 0,
    });
  };

  const handleDelete = (holiday) => {
    setDeleteItem(holiday);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const res = await deleteClassAPI(deleteItem.ClassId);

      if (res.IsSuccessful) {
        loadClasses();
        showSuccessToastMsg("Data deleted successfully");
      } else {
        showErrorToastMsg(res.Message || "Failed to delete data");
      }

      setShowDeleteConfirm(false);
      setDeleteItem(null);
    } catch (error) {
      console.error("Error deleting holiday:", error);
      showErrorToastMsg("Failed to delete data");
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteItem(null);
  };

  return (
    <DashboardLayout
      title="Class Management"
      subtitle="Manage classes and schedules"
    >
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-6">
          Manage Classes
        </h3>

        {/* Add/Edit Form */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              {editingDetails ? "Edit Class" : "Add New Class"}
            </h4>
          </div>
          <div className="p-6">
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-5 gap-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Class Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter class name"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  disabled={editingDetails ? true : false}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  disabled={editingDetails ? true : false}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  disabled={editingDetails ? true : false}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  disabled={editingDetails ? true : false}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  disabled={editingDetails ? true : false}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Class Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="classTypeId"
                  value={formData.classTypeId}
                  onChange={handleChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  disabled={editingDetails ? true : false}
                >
                  <option value="">Select Class Type</option>
                  {classTypes.map((classType) => (
                    <option
                      key={classType.ClassTypeId}
                      value={Number(classType.ClassTypeId)}
                    >
                      {classType.Name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Class fee <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="classFee"
                  value={formData.classFee}
                  onChange={handleChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  disabled={editingDetails ? true : false}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Chat Link
                </label>
                <input
                  type="text"
                  name="whatsAppLink"
                  value={formData.whatsAppLink}
                  onChange={handleChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>

              <div className="flex gap-2 items-end justify-end md:col-span-5">
                {editingDetails && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="text-sm font-medium px-4 py-1.5 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving}
                  className={`text-md font-medium px-4 py-1.5 rounded-md transition-colors ${
                    saving
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-primary-600 text-white hover:bg-primary-700"
                  }`}
                >
                  {saving
                    ? "Saving..."
                    : editingDetails
                      ? "Update"
                      : "Add Class"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Classes Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">Classes List</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Class Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : classes && classes.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      No classes found.
                    </td>
                  </tr>
                ) : (
                  classes &&
                  classes.map((classItem) => (
                    <tr key={classItem.ClassId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {classItem.Name || classItem.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatDate(classItem.StartDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatDate(classItem.EndDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatTime(classItem.StartTime) +
                          " - " +
                          formatTime(classItem.EndTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatAmount(classItem.ClassFee)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {classItem.ClassType.Name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {getStatusBadge(classItem.Status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-4">
                        <button
                          onClick={() =>
                            navigate(
                              `${RoutePaths.ADMIN_CLASS_SCHEDULE_PAGE}?classId=${classItem.ClassId}`,
                            )
                          }
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Schedules
                        </button>

                        <button
                          onClick={() => handleEditDetails(classItem)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => handleDelete(classItem)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showDeleteConfirm && (
          <RemoveConfirmationDialog
            loading={loading}
            description={deleteItem.Name}
            cancelDelete={cancelDelete}
            confirmDelete={confirmDelete}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ClassManagePage;
